/**
 * TerminalView - Main terminal interface
 *
 * Displays:
 * - Terminal tabs (multi-terminal support)
 * - Terminal output panel
 * - Voice interface
 * - Status bar
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TerminalTabs from './TerminalTabs';
import TerminalPanel, { TerminalPanelHandle } from './TerminalPanel';
import StatusBar from './StatusBar';
import FilePanel from './commander/FilePanel';
import ResizableDivider from './commander/ResizableDivider';
import api from '../services/api';

// WebSocket message types from server
interface Transcript {
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
}

interface ToolExecution {
  name: string;
  arguments?: Record<string, any>;
  result?: any;
}

interface Terminal {
  id: string;
  name: string;
  current_disk: string | null;
  current_cwd: string;
  history: Array<{ command: string; result: any; timestamp: string }>;
  last_active: string;
}

interface BrainStatus {
  state: string;
  exists: boolean;
  brain_id?: string;
  relay_connected?: boolean;
}

export default function TerminalView() {
  const { user, logout } = useAuth();
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [activeTerminalId, setActiveTerminalId] = useState<string | null>(null);
  const [brainStatus, setBrainStatus] = useState<BrainStatus>({ state: 'dormant', exists: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // File panel state (Commander mode)
  const [filePanelOpen, setFilePanelOpen] = useState(false);
  const [panelWidths, setPanelWidths] = useState({ left: 50, right: 50 });

  // Voice WebSocket state
  const wsRef = useRef<WebSocket | null>(null);
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [currentTool, setCurrentTool] = useState<ToolExecution | null>(null);
  const [agentWorking, setAgentWorking] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentTerminalIdRef = useRef<string | null>(null);

  // Ref to TerminalPanel for audio playback
  const terminalPanelRef = useRef<TerminalPanelHandle | null>(null);

  // Load terminals on mount
  useEffect(() => {
    loadTerminals();
    loadBrainStatus();
  }, []);

  // Connect to voice WebSocket - only when we have a terminal
  useEffect(() => {
    // Don't connect until we have a terminal_id
    if (!activeTerminalId) {
      console.log('Waiting for terminal before connecting WebSocket');
      return;
    }

    // Small delay to ensure auth token is available
    const timer = setTimeout(() => {
      connectVoiceWebSocket(activeTerminalId);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [activeTerminalId]); // Reconnect when terminal changes

  // Load transcripts from Brain when voice is connected AND terminal is ready
  useEffect(() => {
    console.log('Transcript load effect: voiceConnected=', voiceConnected, 'activeTerminalId=', activeTerminalId);
    if (voiceConnected && activeTerminalId) {
      console.log('Loading transcripts for terminal:', activeTerminalId);
      loadTranscriptsFromBrain(activeTerminalId);
    }
  }, [voiceConnected, activeTerminalId]);

  const connectVoiceWebSocket = (terminalId: string) => {
    const token = localStorage.getItem('ngui_auth_token');
    if (!token) {
      console.log('No token, skipping WebSocket connection');
      return;
    }

    // Track current terminal for reconnects
    currentTerminalIdRef.current = terminalId;

    // Use the correct WebSocket URL based on environment
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/voice`;

    console.log('Connecting to voice WebSocket:', wsUrl, 'with terminal:', terminalId);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('Voice WebSocket connected, sending auth with terminal:', terminalId);
      // Send auth message with terminal_id
      ws.send(JSON.stringify({
        type: 'auth',
        token,
        terminal_id: terminalId
      }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        handleVoiceMessage(msg);
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e);
      }
    };

    ws.onclose = (event) => {
      console.log('Voice WebSocket closed:', event.code, event.reason);
      setVoiceConnected(false);

      // Reconnect after delay (unless intentional close)
      if (event.code !== 1000 && event.code !== 4002 && currentTerminalIdRef.current) {
        const terminalId = currentTerminalIdRef.current;
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect voice WebSocket with terminal:', terminalId);
          connectVoiceWebSocket(terminalId);
        }, 3000);
      }
    };

    ws.onerror = (error) => {
      console.error('Voice WebSocket error:', error);
    };
  };

  const handleVoiceMessage = (msg: any) => {
    switch (msg.type) {
      case 'session.started':
        console.log('Voice session started:', msg.session_id);
        setVoiceConnected(true);
        // Transcripts loaded via useEffect when activeTerminalId is ready
        break;

      case 'transcript':
        // Complete transcript (user or assistant)
        setTranscripts(prev => [...prev, {
          role: msg.role,
          text: msg.text,
          timestamp: new Date()
        }]);
        break;

      case 'transcript.delta':
        // Streaming transcript - could update UI progressively
        break;

      case 'tool.executing':
        // Tool is being executed
        setCurrentTool({
          name: msg.name,
          arguments: msg.arguments
        });
        break;

      case 'tool.result':
        // Tool execution complete
        setCurrentTool(prev => prev ? { ...prev, result: msg.result } : null);
        // Reload terminals to reflect any changes
        loadTerminals();
        break;

      case 'response.done':
        // Response complete
        setCurrentTool(null);
        break;

      case 'error':
        console.error('Voice error:', msg.error || msg.message);
        setError(msg.error || msg.message);
        break;

      case 'audio':
        // Audio data - play via TerminalPanel
        if (terminalPanelRef.current) {
          terminalPanelRef.current.playAudioChunk(msg.audio);
        }
        break;

      case 'agent.progress':
        // Agent is working - show progress message to user
        // type="text" has content, type="tool_use" has tool name
        let progressMsg = '';
        if (msg.data?.type === 'text' && msg.data?.content) {
          progressMsg = msg.data.content;
        } else if (msg.data?.type === 'tool_use' && msg.data?.tool) {
          progressMsg = `Using tool: ${msg.data.tool}`;
        }
        console.log('Agent progress:', msg.data?.type, progressMsg);
        if (progressMsg) {
          setTranscripts(prev => [...prev, {
            role: 'assistant',
            text: progressMsg,
            timestamp: new Date()
          }]);
        }
        setAgentWorking(progressMsg || 'Working...');
        break;

      case 'agent.complete':
        // Agent task completed - show result
        console.log('Agent complete:', msg.data);
        setAgentWorking(null);  // Clear progress indicator
        if (msg.data?.result) {
          setTranscripts(prev => [...prev, {
            role: 'assistant',
            text: msg.data.result,
            timestamp: new Date()
          }]);
        } else if (msg.data?.summary) {
          setTranscripts(prev => [...prev, {
            role: 'assistant',
            text: msg.data.summary,
            timestamp: new Date()
          }]);
        }
        break;

      case 'agent.error':
        // Agent error
        console.error('Agent error:', msg.data?.error);
        setAgentWorking(null);  // Clear progress indicator
        setTranscripts(prev => [...prev, {
          role: 'assistant',
          text: `Error: ${msg.data?.error || 'An error occurred'}`,
          timestamp: new Date()
        }]);
        break;

      case 'agent.question':
        // Agent needs input
        console.log('Agent question:', msg.data?.question);
        setTranscripts(prev => [...prev, {
          role: 'assistant',
          text: msg.data?.question || 'I need more information.',
          timestamp: new Date()
        }]);
        break;

      default:
        console.log('Unhandled voice message:', msg.type);
    }
  };

  const sendTextInput = (text: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'text_input',
        text
      }));
      // Add user message to transcripts
      setTranscripts(prev => [...prev, {
        role: 'user',
        text,
        timestamp: new Date()
      }]);
    } else {
      setError('Not connected to voice service');
    }
  };

  const sendAudio = (base64Audio: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'audio',
        audio: base64Audio
      }));
    }
  };

  const sendCommit = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'commit'
      }));
    }
  };

  const loadTerminals = async () => {
    try {
      const response = await api.get('/terminals');
      const terminalList = response.data.terminals || [];
      setTerminals(terminalList);

      // Set active terminal (first one, or create if none)
      if (terminalList.length > 0) {
        setActiveTerminalId(terminalList[0].id);
      } else {
        // Create default terminal
        await createTerminal();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load terminals');
    } finally {
      setLoading(false);
    }
  };

  const loadBrainStatus = async () => {
    try {
      const response = await api.get('/brain/status');
      setBrainStatus(response.data);
    } catch (err) {
      console.error('Failed to load brain status:', err);
    }
  };

  // Load transcripts from Brain terminal history
  const loadTranscriptsFromBrain = async (terminalId: string) => {
    console.log('loadTranscriptsFromBrain called with terminalId:', terminalId);
    try {
      const payload = {
        command: 'terminal.get_history',
        params: { limit: 100, include_transcripts: true },
        terminal_id: terminalId
      };
      console.log('Sending brain command payload:', JSON.stringify(payload));
      const response = await api.post('/brain/command', payload);

      if (response.data.status === 'ok' && response.data.history) {
        // Extract transcripts from history entries
        const loadedTranscripts: Transcript[] = [];

        for (const entry of response.data.history) {
          if (entry.transcript) {
            // Add user message if present
            if (entry.transcript.user) {
              loadedTranscripts.push({
                role: 'user',
                text: entry.transcript.user,
                timestamp: new Date(entry.timestamp)
              });
            }
            // Add assistant message if present
            if (entry.transcript.assistant) {
              loadedTranscripts.push({
                role: 'assistant',
                text: entry.transcript.assistant,
                timestamp: new Date(entry.timestamp)
              });
            }
          }
        }

        if (loadedTranscripts.length > 0) {
          console.log(`Loaded ${loadedTranscripts.length} transcripts from Brain`);
          setTranscripts(loadedTranscripts);
        }
      }
    } catch (err) {
      console.error('Failed to load transcripts from Brain:', err);
    }
  };

  const createTerminal = async (name?: string) => {
    try {
      const response = await api.post('/terminals', { name });
      const newTerminal = response.data;
      setTerminals(prev => [...prev, newTerminal]);
      setActiveTerminalId(newTerminal.id);
      return newTerminal;
    } catch (err: any) {
      setError(err.message || 'Failed to create terminal');
      throw err;
    }
  };

  const closeTerminal = async (terminalId: string) => {
    try {
      await api.delete(`/terminals/${terminalId}`);
      setTerminals(prev => prev.filter(t => t.id !== terminalId));

      // If closing active terminal, switch to another
      if (activeTerminalId === terminalId) {
        const remaining = terminals.filter(t => t.id !== terminalId);
        if (remaining.length > 0) {
          setActiveTerminalId(remaining[0].id);
        } else {
          // Create new terminal if all closed
          await createTerminal();
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to close terminal');
    }
  };

  const renameTerminal = async (terminalId: string, newName: string) => {
    try {
      const response = await api.patch(`/terminals/${terminalId}`, { name: newName });
      setTerminals(prev =>
        prev.map(t => (t.id === terminalId ? response.data : t))
      );
    } catch (err: any) {
      setError(err.message || 'Failed to rename terminal');
    }
  };

  const startBrain = async () => {
    try {
      setBrainStatus(prev => ({ ...prev, state: 'starting' }));
      await api.post('/brain/start');
      await loadBrainStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to start brain');
      await loadBrainStatus();
    }
  };

  const activeTerminal = terminals.find(t => t.id === activeTerminalId);

  // Handle panel resize
  const handlePanelResize = useCallback((deltaPercent: number) => {
    setPanelWidths(prev => {
      const newLeft = Math.min(80, Math.max(20, prev.left + deltaPercent));
      return { left: newLeft, right: 100 - newLeft };
    });
  }, []);

  if (loading) {
    return (
      <div className="h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading VoxTerm...</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-900 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 bg-gray-800 border-b border-gray-700 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-white">VoxTerm</h1>
          <span className="text-gray-400 text-sm">
            Voice-controlled Voxtron Terminal
          </span>
        </div>
        <div className="flex items-center gap-4">
          {/* File Panel Toggle */}
          <button
            onClick={() => setFilePanelOpen(!filePanelOpen)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors ${
              filePanelOpen
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            title={filePanelOpen ? 'Close file panel' : 'Open file panel'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <span>{activeTerminal?.current_disk || 'Files'}</span>
          </button>

          <span className="text-gray-300">{user?.username}</span>
          <button
            onClick={logout}
            className="text-gray-400 hover:text-white text-sm"
          >
            Logout
          </button>
        </div>
      </header>

      {/* Terminal Tabs */}
      <TerminalTabs
        terminals={terminals}
        activeTerminalId={activeTerminalId}
        onSelectTerminal={setActiveTerminalId}
        onCreateTerminal={createTerminal}
        onCloseTerminal={closeTerminal}
        onRenameTerminal={renameTerminal}
      />

      {/* Main Content */}
      <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {error && (
          <div className="flex-shrink-0 bg-red-900/50 border border-red-500 text-red-200 px-4 py-2 m-2 rounded">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-4 text-red-400 hover:text-red-200"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Bi-Panel Layout */}
        <div className="flex-1 min-h-0 flex flex-row overflow-hidden">
          {/* Left Panel - Terminal (always visible, full width when panel closed) */}
          <div
            style={{ width: filePanelOpen ? `${panelWidths.left}%` : '100%' }}
            className="min-h-0 flex flex-col transition-all duration-200"
          >
            {activeTerminal ? (
              <TerminalPanel
                ref={terminalPanelRef}
                terminal={activeTerminal}
                brainStatus={brainStatus}
                onStartBrain={startBrain}
                voiceConnected={voiceConnected}
                transcripts={transcripts}
                currentTool={currentTool}
                onSendText={sendTextInput}
                onSendAudio={sendAudio}
                onSendCommit={sendCommit}
                wsRef={wsRef}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                No terminal selected
              </div>
            )}
          </div>

          {/* Resizable Divider */}
          {filePanelOpen && (
            <ResizableDivider onResize={handlePanelResize} />
          )}

          {/* Right Panel - File Manager (optional) */}
          {filePanelOpen && (
            <div
              style={{ width: `${panelWidths.right}%` }}
              className="min-h-0 flex flex-col"
            >
              <FilePanel
                disk={activeTerminal?.current_disk || null}
                cwd={activeTerminal?.current_cwd || '/'}
                onClose={() => setFilePanelOpen(false)}
              />
            </div>
          )}
        </div>
      </main>

      {/* Status Bar */}
      <StatusBar
        brainStatus={brainStatus}
        activeTerminal={activeTerminal}
      />
    </div>
  );
}
